import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ---------------------------------------------------------------------------
// Dev-mode localStorage session fallback
//
// In the Replit development environment the reverse proxy strips Set-Cookie
// headers from API responses before they reach the browser, so the httpOnly
// lo_session cookie never lands. As a workaround, the server appends the
// session ID to the URL fragment (#_t=...) after a successful verify. The
// React app reads it here, stashes it in localStorage, and sends it as an
// Authorization: Bearer header on every /api/auth request.
//
// In production (own domain, Replit proxy not involved) the cookie flows
// correctly, localStorage is never written, and the Bearer path is inactive
// because the server only accepts Bearer in NODE_ENV !== 'production'.
// ---------------------------------------------------------------------------

const LS_KEY = '_loa_dev_sid';

function readDevSession() {
  try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
}
export function writeDevSession(sid) {
  try { localStorage.setItem(LS_KEY, sid); } catch {}
}
function clearDevSession() {
  try { localStorage.removeItem(LS_KEY); } catch {}
}

// Build headers for /api/auth requests: include Bearer token if we have one.
function authHeaders() {
  const sid = readDevSession();
  return sid ? { Authorization: `Bearer ${sid}` } : {};
}

// ---------------------------------------------------------------------------
// AuthContext
// ---------------------------------------------------------------------------

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include',
        headers: authHeaders(),
        cache: 'no-store',
      });
      if (res.ok) {
        const json = await res.json();
        setUser(json.user || null);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch once on mount.
  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const signOut = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({}),
      });
    } catch {
      // Ignore errors — clear local state regardless.
    }
    clearDevSession();
    // Clear session-scoped progress data (self-check answers, quiz history)
    // but PRESERVE lo:last_lesson:* so the same user can resume where they
    // left off after signing back in. Only wipe keys that are truly
    // session-specific, not positional resume markers.
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith('lo:last_lesson:')) continue;      // keep resume position
        if (k.startsWith('lo:final_quiz_history:')) continue; // keep quiz pass history
        if (k.startsWith('lo:')) keysToRemove.push(k);
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch (_) {}
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, signOut, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

export function useCurrentUser() {
  const { user } = useAuth();
  return user;
}
